import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useCheckAuthQuery, useCheckActiveSessionQuery } from './slices/usersApiSlice';
import { setCredentials, setLoading, logout } from './slices/authSlice';
import Header from './components/Header';
import HomeScreen from './screens/HomeScreen';
import LoginScreen from './screens/LoginScreen';
import RegisterScreen from './screens/RegisterScreen';
import ProfileScreen from './screens/ProfileScreen';
import AdminDashboard from './screens/AdminDashboard';
import InstituteDashboard from './screens/InstituteDashboard';
import StudentDashboard from './screens/StudentDashboard';
import ContactScreen from './screens/ContactScreen';
import AboutScreen from './screens/AboutScreen';
import PrivacyPolicyScreen from './screens/PrivacyPolicyScreen';
import TermsScreen from './screens/TermsScreen';
import DepartmentManagementScreen from './screens/DepartmentManagementScreen';
import FacultyManagementScreen from './screens/FacultyManagementScreen';
import SubjectManagementScreen from './screens/SubjectManagementScreen';
import InstituteExamCreationScreen from './screens/InstituteExamCreationScreen';
import FacultyDashboard from './screens/FacultyDashboard';
import HODDashboard from './screens/HODDashboard';
import DepartmentReviewScreen from './screens/DepartmentReviewScreen';
import { useTheme } from './context/ThemeContext';
import { toast } from 'react-hot-toast';
import { Analytics } from "@vercel/analytics/react";
import PrivateRoute from './components/PrivateRoute';
import PaperCreationScreen from './screens/PaperCreationScreen';
import PaperViewScreen from './screens/PaperViewScreen';
import ForgotPasswordScreen from './screens/ForgotPasswordScreen';
import ResetPasswordScreen from './screens/ResetPasswordScreen';
import { FullscreenLoader } from './utils/LoaderUtils';

// Create route components here to preserve existing dashboard logic
const AdminRouteWrapper = () => {
  const { userInfo } = useSelector((state) => state.auth);
  return userInfo?.userType === 'admin' ? <AdminDashboard /> : <Navigate to="/login" replace />;
};

const InstituteRouteWrapper = () => {
  const { userInfo } = useSelector((state) => state.auth);
  return userInfo?.userType === 'institute' ? <InstituteDashboard /> : <Navigate to="/login" replace />;
};

const StudentRouteWrapper = () => {
  const { userInfo } = useSelector((state) => state.auth);
  return userInfo?.userType === 'student' ? <StudentDashboard /> : <Navigate to="/login" replace />;
};

const FacultyRouteWrapper = () => {
  const { userInfo } = useSelector((state) => state.auth);
  return userInfo?.userType === 'faculty' ? <FacultyDashboard /> : <Navigate to="/login" replace />;
};

const HODRouteWrapper = () => {
  const { userInfo } = useSelector((state) => state.auth);
  return (userInfo?.userType === 'faculty' && userInfo?.isHOD) ? <HODDashboard /> : <Navigate to="/login" replace />;
};

// Institute management route wrappers
const DepartmentManagementWrapper = () => {
  const { userInfo } = useSelector((state) => state.auth);
  return userInfo?.userType === 'institute' ? <DepartmentManagementScreen /> : <Navigate to="/login" replace />;
};

const FacultyManagementWrapper = () => {
  const { userInfo } = useSelector((state) => state.auth);
  
  // Create a wrapped component with error handling
  const WrappedComponent = () => {
    try {
      return <FacultyManagementScreen />;
    } catch (error) {
      console.error('Error in FacultyManagementScreen:', error);
      
      // Return a fallback UI
      return (
        <div className="min-h-screen flex flex-col items-center justify-center p-6">
          <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg max-w-lg w-full">
            <h2 className="text-xl font-bold text-red-600 dark:text-red-400 mb-4">
              Something went wrong
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              There was an error while loading the faculty management screen. The faculty member may have been created successfully, but we encountered an issue displaying the updated list.
            </p>
            <div className="flex flex-col md:flex-row gap-4">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-violet-600 text-white rounded hover:bg-violet-700"
              >
                Refresh Page
              </button>
              <button
                onClick={() => window.location.href = '/institute/dashboard'}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      );
    }
  };
  
  return userInfo?.userType === 'institute' ? <WrappedComponent /> : <Navigate to="/login" replace />;
};

const SubjectManagementWrapper = () => {
  const { userInfo } = useSelector((state) => state.auth);
  return userInfo?.userType === 'institute' ? <SubjectManagementScreen /> : <Navigate to="/login" replace />;
};

const InstituteExamCreationWrapper = () => {
  const { userInfo } = useSelector((state) => state.auth);
  return userInfo?.userType === 'institute' ? <InstituteExamCreationScreen /> : <Navigate to="/login" replace />;
};

const DepartmentReviewWrapper = ({ paperId }) => {
  const { userInfo } = useSelector((state) => state.auth);
  return (userInfo?.userType === 'faculty' && userInfo?.isHOD) ? <DepartmentReviewScreen /> : <Navigate to="/login" replace />;
};

const PrivateRouteWrapper = ({ children }) => {
  const { userInfo } = useSelector((state) => state.auth);
  return userInfo ? children : <Navigate to="/login" replace />;
};

// Faculty paper route wrappers
const FacultyPaperCreateWrapper = () => {
  const { userInfo } = useSelector((state) => state.auth);
  return userInfo?.userType === 'faculty' ? <PaperCreationScreen /> : <Navigate to="/login" replace />;
};

const FacultyPaperEditWrapper = () => {
  const { userInfo } = useSelector((state) => state.auth);
  return userInfo?.userType === 'faculty' ? <PaperCreationScreen /> : <Navigate to="/login" replace />;
};

const FacultyPaperViewWrapper = () => {
  const { userInfo } = useSelector((state) => state.auth);
  return userInfo?.userType === 'faculty' ? <PaperViewScreen /> : <Navigate to="/login" replace />;
};

const HODPaperReviewWrapper = () => {
  const { userInfo } = useSelector((state) => state.auth);
  return (userInfo?.userType === 'faculty' && userInfo?.isHOD) ? <DepartmentReviewScreen /> : <Navigate to="/login" replace />;
};

const App = () => {
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const dispatch = useDispatch();
  const { data: authData, isLoading, isError } = useCheckAuthQuery();
  const { userInfo } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  
  // Only check for active sessions if the user is already authenticated
  const { 
    data: sessionData, 
    isError: isSessionError, 
    error: sessionError 
  } = useCheckActiveSessionQuery(undefined, {
    skip: !userInfo, // Skip this query if user is not authenticated
  });

  // Handle initial authentication
  useEffect(() => {
    if (authData) {
      dispatch(setCredentials(authData));
    } else if (isError) {
      dispatch(logout());
    }
  }, [authData, isError, dispatch]);
  
  // Handle session checking for authenticated users
  useEffect(() => {
    if (isSessionError && userInfo) {
      // Check if this is a session expiration error
      if (sessionError?.data?.code === 'SESSION_EXPIRED') {
        // Show a single toast message about session being logged out
        toast.error('Your session was logged out from another device', {
          duration: 5000,
          icon: '⚠️',
        });
        
        // Just navigate to login without adding error parameter
        navigate('/login');
        
        // Log out the user
        dispatch(logout());
      }
    }
  }, [isSessionError, sessionError, userInfo, dispatch, navigate]);

  if (isLoading) {
    return <FullscreenLoader text="Loading application..." />;
  }

  const getRedirectPath = (userType, isHOD) => {
    switch (userType) {
      case 'admin':
        return '/admin/dashboard';
      case 'institute':
        return '/institute/dashboard';
      case 'faculty':
        return isHOD ? '/department/dashboard' : '/faculty/dashboard';
      case 'student':
        return '/student/dashboard';
      default:
        return '/';
    }
  };

  return (
    <div className={isDarkMode ? 'dark' : ''}>
      <Analytics />
      <Header />
      <main className="min-h-screen">
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<HomeScreen />} />
          <Route path="/about" element={<AboutScreen />} />
          <Route path="/contact" element={<ContactScreen />} />
          
          {/* Auth routes */}
          <Route
            path="/login"
            element={
              userInfo ? (
                <Navigate to={getRedirectPath(userInfo.userType, userInfo.isHOD)} replace />
              ) : (
                <LoginScreen />
              )
            }
          />
          <Route
            path="/register"
            element={
              userInfo ? (
                <Navigate to={getRedirectPath(userInfo.userType, userInfo.isHOD)} replace />
              ) : (
                <RegisterScreen />
              )
            }
          />
          <Route
            path="/forgot-password"
            element={
              userInfo ? (
                <Navigate to={getRedirectPath(userInfo.userType, userInfo.isHOD)} replace />
              ) : (
                <ForgotPasswordScreen />
              )
            }
          />
          <Route
            path="/reset-password/:token"
            element={
              userInfo ? (
                <Navigate to={getRedirectPath(userInfo.userType, userInfo.isHOD)} replace />
              ) : (
                <ResetPasswordScreen />
              )
            }
          />
          
          {/* Protected routes */}
          <Route
            path="/profile"
            element={
              <PrivateRouteWrapper>
                <ProfileScreen />
              </PrivateRouteWrapper>
            }
          />

          {/* Role-specific routes */}
          <Route path="/admin/dashboard" element={<AdminRouteWrapper />} />
          <Route path="/institute/dashboard" element={<InstituteRouteWrapper />} />
          <Route path="/student/dashboard" element={<StudentRouteWrapper />} />
          <Route path="/faculty/dashboard" element={<FacultyRouteWrapper />} />
          <Route path="/department/dashboard" element={<HODRouteWrapper />} />
          
          {/* Faculty Paper Routes */}
          <Route path="/faculty/papers/create" element={
            <PrivateRouteWrapper>
              <FacultyPaperCreateWrapper />
            </PrivateRouteWrapper>
          } />
          <Route path="/faculty/papers/edit/:id" element={
            <PrivateRouteWrapper>
              <FacultyPaperEditWrapper />
            </PrivateRouteWrapper>
          } />
          <Route path="/faculty/papers/view/:id" element={
            <PrivateRouteWrapper>
              <FacultyPaperViewWrapper />
            </PrivateRouteWrapper>
          } />
          
          {/* Route for faculty to access InstituteExamCreationScreen */}
          <Route path="/institute/exams/create" element={
            <PrivateRouteWrapper>
              <InstituteExamCreationScreen />
            </PrivateRouteWrapper>
          } />
          
          {/* Department Head Review Routes */}
          <Route path="/department/papers/review/:paperId" element={
            <PrivateRouteWrapper>
              <HODPaperReviewWrapper />
            </PrivateRouteWrapper>
          } />
          
          {/* Institute Management Routes */}
          <Route path="/institute/departments" element={<DepartmentManagementWrapper />} />
          <Route path="/institute/faculty" element={<FacultyManagementWrapper />} />
          <Route path="/institute/subjects" element={<SubjectManagementWrapper />} />
          <Route path="/institute/exam/create" element={<InstituteExamCreationWrapper />} />
          <Route path="/institute/exam/edit/:id" element={<InstituteExamCreationWrapper />} />
          
          {/* Add these new routes */}
          <Route path="/privacy-policy" element={<PrivacyPolicyScreen />} />
          <Route path="/terms-of-service" element={<TermsScreen />} />
            
          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
};

export default App;
